import { useState, useEffect } from "react";
import { Info } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import { getCurrentUser } from "../../utils/Api";
import {
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser,
} from "firebase/auth";
import { doc, deleteDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import Config from "../../Config";

import FreePlanUpgradeModal from "../../modals/FreePlanUpgradeModal";
import UsageLimitsInfoModal from "../../modals/UsageLimitsInfoModal";
import DeleteAccountModal, {
  OrganizationToDelete,
} from "../../modals/DeleteAccountModal";
import UserNavbar from "../../components/UserNavbar";
import Footer from "../../components/Footer";

interface UsageRowProps {
  label: string;
  tooltip: string;
  subtitle: string;
  value: number;
  max: number;
  color?: string;
}

const UsageRow: React.FC<UsageRowProps> = ({
  label,
  tooltip,
  subtitle,
  value,
  max,
  color = "bg-[#1d365d]",
}) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <div className="flex items-center gap-1.5">
        <span className="font-medium text-gray-900">{label}</span>
        <div className="relative group">
          <Info size={14} className="text-gray-400 cursor-default" />
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
            {tooltip}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
          </div>
        </div>
      </div>
      <span className="text-sm text-gray-500">
        {value}/{max}
      </span>
    </div>
    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div
        className={`h-full ${color} rounded-full transition-all duration-300`}
        style={{ width: `${Math.min((value / max) * 100, 100)}%` }}
      />
    </div>
    <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
  </div>
);

interface SubscriptionStatus {
  subscription_id: string | null;
  status: string | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
}

const Settings: React.FC = () => {
  const { user, logout } = useAuth();

  const [pagesUploadedToday, setPagesUploadedToday] = useState<number>(0);
  const [dailyLimit, setDailyLimit] = useState<number>(10);
  const [monthlyPagesUploaded, setMonthlyPagesUploaded] = useState<number>(0);
  const [monthlyLimit, setMonthlyLimit] = useState<number>(0);
  const [accumulatedPagesBank, setAccumulatedPagesBank] = useState<number>(0);
  const [accountType, setAccountType] = useState<string>("free");
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(
    null,
  );
  const [portalLoading, setPortalLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [organizationsToDelete, setOrganizationsToDelete] = useState<
    OrganizationToDelete[]
  >([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showUsageLimitsInfoModal, setShowUsageLimitsInfoModal] =
    useState(false);

  const APP_VERSION = Config.VERSION;

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const response = await getCurrentUser();
        setAccountType(response.data.account_type);
        setPagesUploadedToday(response.data.pages_uploaded_today);
        setDailyLimit(response.data.daily_limit);
        setMonthlyPagesUploaded(response.data.monthly_pages_uploaded);
        setMonthlyLimit(response.data.monthly_limit);
        setAccumulatedPagesBank(response.data.accumulated_pages_bank);
      } catch (error) {
        console.error("Failed to load user info:", error);
      }
    };
    loadUserInfo();
  }, []);

  useEffect(() => {
    const loadSubscription = async () => {
      const token = localStorage.getItem("access_token");
      if (!token) return;
      try {
        const res = await fetch(`${Config.API_URL}/api/billing/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setSubscription(await res.json());
      } catch {}
    };
    loadSubscription();
  }, []);

  const openPortal = async () => {
    setPortalLoading(true);
    const token = localStorage.getItem("access_token");
    try {
      const res = await fetch(`${Config.API_URL}/api/billing/portal`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const { url } = await res.json();
        window.location.href = url;
      }
    } finally {
      setPortalLoading(false);
    }
  };

  const getResetTime = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const diffMs = tomorrow.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${diffHours}h ${diffMins}m`;
  };

  const fetchOrganizationsToDelete = async () => {
    setLoadingOrganizations(true);
    try {
      const backendToken = localStorage.getItem("access_token");
      if (!backendToken) {
        return;
      }

      const response = await fetch(
        `${Config.API_URL}/api/auth/organizations-to-delete`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${backendToken}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setOrganizationsToDelete(data.organizations || []);
      }
    } catch (error) {
      console.error("Error fetching organizations to delete:", error);
    } finally {
      setLoadingOrganizations(false);
    }
  };

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${Config.API_URL}/api/auth/export-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "my-data.zip";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
    } finally {
      setExportLoading(false);
    }
  };

  const handleOpenDeleteModal = () => {
    setDeleteModalOpen(true);
    fetchOrganizationsToDelete();
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !user.email) {
      setDeleteError("User not authenticated");
      return;
    }

    if (!isPsdiUser && !deletePassword) {
      setDeleteError("Please enter your password to confirm");
      return;
    }

    setDeleteLoading(true);
    setDeleteError("");

    try {
      if (!isPsdiUser) {
        const credential = EmailAuthProvider.credential(
          user.email,
          deletePassword,
        );
        await reauthenticateWithCredential(user, credential);
      }

      const backendToken = localStorage.getItem("access_token");

      if (!backendToken) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(
        `${Config.API_URL}/api/auth/delete-account`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${backendToken}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403) {
          throw new Error(
            errorData.detail ||
              "Cannot delete account due to organization admin requirements",
          );
        }
        throw new Error(
          errorData.detail || "Failed to delete account from backend",
        );
      }

      if (!isPsdiUser && user.uid) {
        const userDocRef = doc(db, "users", user.uid);
        await deleteDoc(userDocRef);
        await deleteUser(user);
      }

      await logout();
      window.location.href = "/";
    } catch (error: any) {
      console.error("Error deleting account:", error);
      if (
        error.code === "auth/invalid-credential" ||
        error.code === "auth/wrong-password"
      ) {
        setDeleteError("Incorrect password. Please try again.");
      } else if (error.code === "auth/too-many-requests") {
        setDeleteError("Too many failed attempts. Please try again later.");
      } else {
        setDeleteError(
          error.message || "Failed to delete account. Please try again.",
        );
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="w-1/2 max-[600px]:w-full max-[600px]:px-4 mx-auto mt-16 mb-10 text-left">
        <h3 className="text-xl font-semibold tracking-tight text-gray-900">
          Settings
        </h3>
        <p className="text-base text-gray-500 mt-0.5">Loading...</p>
      </div>
    );
  }

  const isPsdiUser = user?.uid === "keycloak-user";

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <UserNavbar />
      <div className="flex-1 w-1/2 max-[600px]:w-full max-[600px]:px-4 mx-auto mt-16 mb-20 text-left">
        <h3 className="text-xl font-semibold tracking-tight text-gray-900">
          Settings
        </h3>
        <p className="text-base text-gray-500 mt-0.5">
          Manage your account settings
        </p>

        {/* usage (for free, individual, and PSDI accounts) */}
        {(accountType === "free" ||
          accountType === "individual" ||
          isPsdiUser) && (
          <div className="mt-8">
            <p className="text-xs font-medium uppercase tracking-widest text-gray-400 mb-3">
              Usage
            </p>
            <div className="border border-gray-200 rounded-lg p-5 space-y-4">
              {/* Pages today — effective limit includes rollover bank */}
              <UsageRow
                label="Pages today"
                tooltip="Pages you can upload today, including any unused pages rolled over from previous days."
                subtitle={`Resets in ${getResetTime()}`}
                value={pagesUploadedToday}
                max={dailyLimit + accumulatedPagesBank}
              />

              {/* Pages this month — individual only */}
              {accountType === "individual" && monthlyLimit > 0 && (
                <UsageRow
                  label="Pages this month"
                  tooltip="Total pages uploaded in your current billing period. Resets to 3,000 each month."
                  subtitle={`${monthlyLimit - monthlyPagesUploaded} remaining`}
                  value={monthlyPagesUploaded}
                  max={monthlyLimit}
                />
              )}

              {accountType === "free" && !isPsdiUser && (
                <p className="text-sm text-gray-400">
                  Want more pages? Upgrade to Individual.{" "}
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="text-[#1d365d] hover:text-[#2d4a75] underline"
                  >
                    Learn more
                  </button>
                </p>
              )}

              <div className="pt-3 border-t border-gray-100">
                <button
                  onClick={() => setShowUsageLimitsInfoModal(true)}
                  className="text-sm text-gray-400 hover:text-gray-600 transition-colors underline"
                >
                  Learn more about usage limits
                </button>
              </div>
            </div>
          </div>
        )}

        {/* subscription */}
        <div className="mt-8">
          <p className="text-xs font-medium uppercase tracking-widest text-gray-400 mb-3">
            Subscription
          </p>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            {accountType === "individual" ? (
              <>
                <div className="flex items-center justify-between px-5 py-4 max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-1">
                  <span className="text-sm text-gray-500">Plan</span>
                  <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded">
                    Individual · £25/month
                  </span>
                </div>
                <div className="flex items-center justify-between px-5 py-4 max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-1">
                  <span className="text-sm text-gray-500">Status</span>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border capitalize ${
                      subscription?.cancel_at_period_end
                        ? "text-orange-600 bg-orange-50 border-orange-200"
                        : subscription?.status === "active"
                          ? "text-green-600 bg-green-50 border-green-200"
                          : subscription?.status === "past_due"
                            ? "text-yellow-600 bg-yellow-50 border-yellow-200"
                            : "text-gray-500 bg-gray-50 border-gray-200"
                    }`}
                  >
                    {subscription?.cancel_at_period_end
                      ? "Cancelling"
                      : (subscription?.status ?? "active")}
                  </span>
                </div>
                {subscription?.current_period_end && (
                  <div className="flex items-center justify-between px-5 py-4 max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-1">
                    <span className="text-sm text-gray-500">
                      {subscription.cancel_at_period_end
                        ? "Plan ends on"
                        : "Next billing date"}
                    </span>
                    <span className="text-sm text-gray-700">
                      {new Date(
                        subscription.current_period_end * 1000,
                      ).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                )}
                <div className="px-5 py-4">
                  <button
                    onClick={openPortal}
                    disabled={portalLoading}
                    className="px-4 py-2 text-sm font-medium text-[#1d365d] border border-[#1d365d] rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {portalLoading ? "Opening..." : "Manage subscription"}
                  </button>
                  <p className="text-xs text-gray-400 mt-2">
                    Cancel, update payment method or view invoices via Stripe
                  </p>
                </div>
              </>
            ) : accountType === "organization" ? (
              <div className="px-5 py-4">
                <p className="text-sm text-gray-500">
                  Your subscription is managed by your organisation admin.
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-between px-5 py-4 max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-3">
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    Free plan
                  </span>
                  <p className="text-sm text-gray-400 mt-0.5">
                    Upgrade to unlock 100 pages/day and all features
                  </p>
                </div>
                <Link
                  to="/upgrade"
                  className="px-4 py-2 text-sm font-medium text-white bg-[#1d365d] rounded-lg hover:bg-[#16294a] transition-colors whitespace-nowrap"
                >
                  Upgrade to Individual
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* account */}
        {!isPsdiUser && (
          <div className="mt-8">
            <p className="text-xs font-medium uppercase tracking-widest text-gray-400 mb-3">
              Account
            </p>
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
              <div className="flex items-center justify-between px-5 py-4 max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-1">
                <span className="text-sm text-gray-500">Account created</span>
                <span className="text-sm text-gray-700">
                  {user.metadata?.creationTime
                    ? new Date(user.metadata.creationTime).toLocaleDateString(
                        "en-GB",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        },
                      )
                    : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between px-5 py-4 max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-1">
                <span className="text-sm text-gray-500">Last sign in</span>
                <span className="text-sm text-gray-700">
                  {user.metadata?.lastSignInTime
                    ? new Date(user.metadata.lastSignInTime).toLocaleDateString(
                        "en-GB",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        },
                      )
                    : "—"}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* data & privacy */}
        <div className="mt-8">
          <p className="text-xs font-medium uppercase tracking-widest text-gray-400 mb-3">
            Data &amp; privacy
          </p>
          <div className="border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between px-5 py-4 max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-3">
              <div>
                <span className="text-sm font-medium text-gray-900">
                  Download your data
                </span>
                <p className="text-sm text-gray-400 mt-0.5">
                  Export a copy of all data we hold about you
                </p>
              </div>
              <button
                onClick={handleExportData}
                disabled={exportLoading}
                className="px-4 py-2 text-sm font-medium text-[#1d365d] border border-[#1d365d] rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {exportLoading ? "Preparing..." : "Download your data"}
              </button>
            </div>
          </div>
        </div>

        {/* danger zone */}
        <div className="mt-8">
          <p className="text-xs font-medium uppercase tracking-widest text-gray-400 mb-3">
            Danger zone
          </p>
          <div className="border border-red-200 rounded-lg bg-red-50/50">
            <div className="flex items-center justify-between px-5 py-4 max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-3">
              <div>
                <span className="text-sm font-medium text-red-700">
                  Delete account
                </span>
                <p className="text-sm text-red-500 mt-0.5">
                  Permanently delete your account and all associated data
                </p>
              </div>
              <button
                className="px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors max-[600px]:w-full"
                onClick={handleOpenDeleteModal}
              >
                Delete account
              </button>
            </div>
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-gray-400">
            Made with care for your lab notes · Data Revival · {APP_VERSION}
          </p>
        </div>

        <FreePlanUpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
        />
        <UsageLimitsInfoModal
          isOpen={showUsageLimitsInfoModal}
          setIsOpen={setShowUsageLimitsInfoModal}
        />
        <DeleteAccountModal
          open={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setDeletePassword("");
            setDeleteError("");
          }}
          onSubmit={handleDeleteAccount}
          deletePassword={deletePassword}
          setDeletePassword={setDeletePassword}
          deleteError={deleteError}
          deleteLoading={deleteLoading}
          organizationsToDelete={organizationsToDelete}
          loadingOrganizations={loadingOrganizations}
          isPsdiUser={isPsdiUser}
        />
      </div>
      <Footer />
    </div>
  );
};

export default Settings;
